import React from "react";
import ReactSelect, {
  components,
  GroupProps,
  MenuProps,
  MultiValueRemoveProps,
} from "react-select";
import { Iconography } from "@hid-galaxy-ui/galaxy-react";
import {
  AdminHomeDropdownOption,
  adminHomeDropdownMenuPortalTarget,
  adminHomeMultiDropdownStyles,
} from "../../utils/adminHomeDropdownStyles";
import type { AdminHomeFilterOptionGroup } from "../../utils/filterOptions";
import {
  applyCustomYearToFilters,
  parseCustomYearInput,
} from "../../utils/filterOptions";

type AdminHomeFilterDropdownProps = {
  filterOptionGroups: AdminHomeFilterOptionGroup[];
  filters: AdminHomeDropdownOption[];
  basedOnValue: string | null;
  isDisabled: boolean;
  onFiltersChange: (options: AdminHomeDropdownOption[]) => void;
};

const DropdownIndicator = () => (
  <span className="hid-dropdown__custom-icon hid-dropdown__chevron-icon">
    <Iconography icon="angleDown" />
  </span>
);

const MultiValueRemove = (props: MultiValueRemoveProps<AdminHomeDropdownOption>) => (
  <components.MultiValueRemove {...props}>
    <Iconography icon="xmark" />
  </components.MultiValueRemove>
);

type YearInputPanelProps = {
  inputRef: React.RefObject<HTMLInputElement>;
  panelRef: React.RefObject<HTMLDivElement>;
  error: string;
  onApply: (value: string) => void;
  onClearError: () => void;
  onKeepMenuOpen: () => void;
};

const YearInputPanel: React.FC<YearInputPanelProps> = ({
  inputRef,
  panelRef,
  error,
  onApply,
  onClearError,
  onKeepMenuOpen,
}) => {
  const stopMenuClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    onKeepMenuOpen();
  };

  return (
    <div
      ref={panelRef}
      className="admin-home-filter-year-input"
      onMouseDown={stopMenuClose}
      onPointerDown={stopMenuClose}
    >
      <label
        className="admin-home-filter-year-input__label"
        htmlFor="admin-home-custom-year"
      >
        Enter year
      </label>
      <div className="admin-home-filter-year-input__row">
        <input
          ref={inputRef}
          id="admin-home-custom-year"
          className="admin-home-filter-year-input__field"
          type="text"
          inputMode="numeric"
          maxLength={4}
          placeholder="e.g. 2020"
          defaultValue=""
          autoComplete="off"
          onMouseDown={stopMenuClose}
          onPointerDown={stopMenuClose}
          onFocus={onKeepMenuOpen}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") {
              event.preventDefault();
              onApply((event.target as HTMLInputElement).value);
            }
          }}
          onKeyUp={(event) => event.stopPropagation()}
          onChange={() => onClearError()}
        />
        <button
          type="button"
          className="admin-home-filter-year-input__apply"
          onMouseDown={stopMenuClose}
          onPointerDown={stopMenuClose}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onApply(inputRef.current?.value ?? "");
          }}
        >
          Apply
        </button>
      </div>
      {error ? (
        <span className="admin-home-filter-year-input__error">{error}</span>
      ) : null}
    </div>
  );
};

const AdminHomeFilterDropdown: React.FC<AdminHomeFilterDropdownProps> = ({
  filterOptionGroups,
  filters,
  basedOnValue,
  isDisabled,
  onFiltersChange,
}) => {
  const yearInputRef = React.useRef<HTMLInputElement>(null);
  const yearPanelRef = React.useRef<HTMLDivElement>(null);
  const keepMenuOpenRef = React.useRef(false);
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);
  const [yearInputError, setYearInputError] = React.useState("");

  const keepMenuOpen = React.useCallback(() => {
    keepMenuOpenRef.current = true;
    setMenuIsOpen(true);
  }, []);

  const handleMenuClose = React.useCallback(() => {
    if (keepMenuOpenRef.current) {
      keepMenuOpenRef.current = false;
      return;
    }

    const activeElement = document.activeElement;
    if (
      yearInputRef.current === activeElement ||
      yearPanelRef.current?.contains(activeElement)
    ) {
      return;
    }

    setMenuIsOpen(false);
  }, []);

  const applyCustomYear = React.useCallback(
    (rawValue: string) => {
      const year = parseCustomYearInput(rawValue);

      if (year == null) {
        setYearInputError("Enter a valid 4-digit year");
        keepMenuOpen();
        yearInputRef.current?.focus();
        return;
      }

      setYearInputError("");
      onFiltersChange(applyCustomYearToFilters(filters, basedOnValue, year));

      if (yearInputRef.current) {
        yearInputRef.current.value = "";
      }

      keepMenuOpen();
    },
    [basedOnValue, filters, keepMenuOpen, onFiltersChange]
  );

  const Menu = React.useCallback(
    (props: MenuProps<AdminHomeDropdownOption, true>) => (
      <components.Menu
        {...props}
        innerProps={{
          ...props.innerProps,
          onMouseDown: (event) => {
            event.preventDefault();
            props.innerProps.onMouseDown?.(event);
          },
        }}
      />
    ),
    []
  );

  const Group = React.useCallback(
    (props: GroupProps<AdminHomeDropdownOption, true>) => {
      const groupData = props.data as AdminHomeFilterOptionGroup;

      if (groupData.label !== "Year") {
        return <components.Group {...props} />;
      }

      return (
        <components.Group {...props}>
          <YearInputPanel
            inputRef={yearInputRef}
            panelRef={yearPanelRef}
            error={yearInputError}
            onApply={applyCustomYear}
            onClearError={() => setYearInputError("")}
            onKeepMenuOpen={keepMenuOpen}
          />
          {props.children}
        </components.Group>
      );
    },
    [applyCustomYear, keepMenuOpen, yearInputError]
  );

  return (
    <ReactSelect
      inputId="admin-home-filter"
      placeholder="Select"
      className="hid-dropdown"
      classNamePrefix="hid-dropdown"
      options={filterOptionGroups}
      value={filters}
      onChange={(data) => {
        onFiltersChange(Array.isArray(data) ? [...data] : []);
        setMenuIsOpen(true);
      }}
      isMulti
      isClearable
      isSearchable={false}
      isDisabled={isDisabled}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      tabSelectsValue={false}
      hideSelectedOptions={false}
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={handleMenuClose}
      menuPortalTarget={adminHomeDropdownMenuPortalTarget}
      styles={adminHomeMultiDropdownStyles}
      components={
        {
          DropdownIndicator,
          ClearIndicator: components.ClearIndicator,
          MultiValueRemove,
          LoadingIndicator: (): null => null,
          Menu,
          Group,
        } as unknown as typeof components
      }
    />
  );
};

export default AdminHomeFilterDropdown;
